import React, { useCallback, useEffect, useRef, useState } from 'react';
import { StationeryTemplateCategoryEnum } from '@graphql/generated-base';
import { useCardTelemetry } from '@apps/card/Card.telemetry';
import { ChevronBack, ChevronRight } from '@withjoy/joykit/icons';
import { useDesignGalleryFiltersContext } from '../../DesignGalleryFiltersProvider';
import { STYLE_FACET } from '../Filters/Filters.constants';
import { useTranslation } from '@shared/core';
import { Section, GalleryHeading, ScrollWrapper, ScrollContainer, ArrowButton, StyleItem, ThumbnailWrapper, Thumbnail, Label } from './StyleShowcase.styles';
import { createSafeResizeObserver } from '@shared/utils/createSafeResizeObserver';

/**
 * The groups that have a chip (see `STYLE_DEFINITIONS`); closed so every chip must have a label.
 * Each should be a key of the index's `keywordGroups` (themes.json keeps it a superset of these).
 * Not checked at runtime: the bundle loads client-side, so filtering chips on it would hide the
 * row until then. A key the bundle drops still renders, and its chip filters to no results.
 */
type ShowcaseGroupKey = 'botanical' | 'floral' | 'elegant' | 'destination' | 'typographic' | 'rustic' | 'minimal' | 'vintage' | 'whimsical';

type StyleDefinition = Readonly<{
  /** Style keyword group (`keywordGroups` in the embeddings bundle) to toggle in the filter system. */
  groupKey: ShowcaseGroupKey;
  /** Asset filename per category (without extension). Falls back to `assetKey` when no override. */
  assetKey: string;
  assetKeyOverrides?: Partial<Record<StationeryTemplateCategoryEnum, string>>;
}>;

const STYLE_DEFINITIONS: StyleDefinition[] = [
  { groupKey: 'botanical', assetKey: 'botanical' },
  {
    groupKey: 'floral',
    assetKey: 'floral',
    assetKeyOverrides: { [StationeryTemplateCategoryEnum.invitation]: 'floral-v2' },
  },
  { groupKey: 'elegant', assetKey: 'elegant_classic' },
  { groupKey: 'destination', assetKey: 'destination-v2' },
  { groupKey: 'typographic', assetKey: 'typographic' },
  {
    groupKey: 'rustic',
    assetKey: 'rustic-v2',
    assetKeyOverrides: { [StationeryTemplateCategoryEnum.invitation]: 'rustic-v3' },
  },
  { groupKey: 'minimal', assetKey: 'minimal' },
  { groupKey: 'vintage', assetKey: 'vintage' },
  { groupKey: 'whimsical', assetKey: 'whimsical' },
];

const CATEGORY_TO_ASSET_PATH: Partial<Record<StationeryTemplateCategoryEnum, string>> = {
  [StationeryTemplateCategoryEnum.saveTheDate]: 'save-the-date-gallery',
  [StationeryTemplateCategoryEnum.invitation]: 'invites-gallery',
};

const SUPPORTED_CATEGORIES = new Set([StationeryTemplateCategoryEnum.saveTheDate, StationeryTemplateCategoryEnum.invitation]);

const S3_BASE = 'https://withjoy.com/assets/public/marcom-prod';

function getAssetUrl(category: StationeryTemplateCategoryEnum, style: StyleDefinition): string {
  const galleryPath = CATEGORY_TO_ASSET_PATH[category];
  const key = style.assetKeyOverrides?.[category] ?? style.assetKey;
  return `${S3_BASE}/${galleryPath}/styles/${key}.png`;
}

/**
 * Build the composite code for a style definition.
 * e.g. groupKey: 'classic' → 'style:classic'
 */
function toCompositeCode(style: StyleDefinition): string {
  return `${STYLE_FACET}:${style.groupKey}`;
}

function isStyleActive(activeFilters: ReadonlySet<string>, style: StyleDefinition): boolean {
  return activeFilters.has(toCompositeCode(style));
}

const SCROLL_AMOUNT = 240;

type StyleShowcaseProps = Readonly<{
  category: StationeryTemplateCategoryEnum;
}>;

export const StyleShowcase: React.FC<StyleShowcaseProps> = ({ category }) => {
  const { activePrintFilters: activeFilters, setActiveFilterGroup } = useDesignGalleryFiltersContext();
  const { buttonInteracted } = useCardTelemetry();
  const { tq } = useTranslation('stationery');
  // `tq` must sit lexically in the component for the extractor, so labels live here, not in
  // `STYLE_DEFINITIONS`. The record is exhaustive over `ShowcaseGroupKey`, so no chip lacks one.
  const styleLabels: Record<ShowcaseGroupKey, string> = {
    botanical: tq`Botanical`,
    floral: tq`Floral`,
    elegant: tq`Elegant`,
    destination: tq`Destination`,
    typographic: tq`Typographic`,
    rustic: tq`Rustic`,
    minimal: tq`Minimal`,
    vintage: tq`Vintage`,
    whimsical: tq`Whimsical`,
  };
  const labelFor = (style: StyleDefinition) => styleLabels[style.groupKey];
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const updateArrowVisibility = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setShowLeftArrow(scrollLeft > 2);
    setShowRightArrow(scrollLeft + clientWidth < scrollWidth - 2);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    updateArrowVisibility();

    const observer = createSafeResizeObserver(updateArrowVisibility, { throttle: false });
    observer.observe(el);
    el.addEventListener('scroll', updateArrowVisibility, { passive: true });

    return () => {
      observer.disconnect();
      el.removeEventListener('scroll', updateArrowVisibility);
    };
  }, [updateArrowVisibility]);

  if (!SUPPORTED_CATEGORIES.has(category)) {
    return null;
  }

  const heading =
    category === StationeryTemplateCategoryEnum.saveTheDate ? tq`Save the Date Cards` : category === StationeryTemplateCategoryEnum.invitation ? tq`Wedding Invitations` : null;

  /** SEO-friendly alt text: e.g. "rustic save the date cards" or "floral wedding invitations" */
  const getStyleAlt = (styleLabel: string) => {
    const label = styleLabel.toLowerCase();
    if (category === StationeryTemplateCategoryEnum.saveTheDate) return tq`${label} save the date cards`;
    if (category === StationeryTemplateCategoryEnum.invitation) return tq`${label} wedding invitations`;
    return tq`${label} cards`;
  };

  const handleScroll = (dir: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({
      left: dir === 'left' ? -SCROLL_AMOUNT : SCROLL_AMOUNT,
      behavior: 'smooth',
    });
  };

  const handleStyleClick = (style: StyleDefinition) => {
    const isActive = isStyleActive(activeFilters, style);
    const code = toCompositeCode(style);

    buttonInteracted({
      name: 'designsGallery_styleShowcaseClicked',
      facet: 'style',
      code,
      isChecked: !isActive,
    });

    // Single-select: the clicked style replaces the whole style group, and
    // clicking the active style clears it. One call so it is one history entry.
    setActiveFilterGroup(STYLE_FACET, isActive ? [] : [style.groupKey]);
  };

  return (
    <Section>
      {heading && <GalleryHeading>{heading}</GalleryHeading>}
      {showLeftArrow && (
        <ArrowButton
          $dir="left"
          onClick={() => handleScroll('left')}
          aria-label={tq`Scroll styles left`}
        >
          <ChevronBack
            aria-hidden
            title={undefined}
            size={20}
          />
        </ArrowButton>
      )}
      <ScrollWrapper>
        <ScrollContainer
          ref={scrollRef}
          role="group"
          aria-label={tq`Filter by style`}
        >
          {STYLE_DEFINITIONS.map(style => {
            const isActive = isStyleActive(activeFilters, style);
            return (
              <StyleItem
                key={style.assetKey}
                onClick={() => handleStyleClick(style)}
                aria-pressed={isActive}
                aria-label={tq`Filter by ${labelFor(style)} style`}
              >
                <ThumbnailWrapper $isActive={isActive}>
                  <Thumbnail
                    src={getAssetUrl(category, style)}
                    alt={getStyleAlt(labelFor(style))}
                    loading="lazy"
                  />
                </ThumbnailWrapper>
                <Label $isActive={isActive}>{labelFor(style)}</Label>
              </StyleItem>
            );
          })}
        </ScrollContainer>
      </ScrollWrapper>
      {showRightArrow && (
        <ArrowButton
          $dir="right"
          onClick={() => handleScroll('right')}
          aria-label={tq`Scroll styles right`}
        >
          <ChevronRight
            aria-hidden
            title={undefined}
            size={20}
          />
        </ArrowButton>
      )}
    </Section>
  );
};
